/**
 * Search Drawer Manager System
 *
 * Sistema modular para gerenciamento do drawer de busca.
 * Segue principios SOLID para codigo manutenivel e reutilizavel.
 * Integra-se com o HeaderManager existente via EventBus.
 *
 * @version 1.0.0
 * @author Wicomm
 *
 * Arquitetura:
 * ┌─────────────────────────────────────────────────────────────┐
 * │                  SearchDrawerManager                        │
 * │                 (Single Responsibility)                     │
 * ├─────────────────────────────────────────────────────────────┤
 * │  - Gerencia drawer de busca                                 │
 * │  - Comunica via EventBus com HeaderManager                  │
 * │  - Fecha menu drawer ao abrir                               │
 * │  - Z-index maior que menu drawer                            │
 * └─────────────────────────────────────────────────────────────┘
 *
 * Principios SOLID aplicados:
 * - SRP: Apenas gerencia o drawer de busca
 * - OCP: Extensivel via configuracao, fechado para modificacao
 * - LSP: Pode ser substituido por qualquer drawer com mesma interface
 * - ISP: Interface minima (open, close, toggle, destroy)
 * - DIP: Depende de abstracoes (eventos), nao implementacoes
 */

/* ==========================================================================
   SEARCH DRAWER MANAGER
   Gerenciamento do drawer de busca
   ========================================================================== */

/**
 * SearchDrawerManager - Responsabilidade Unica: Gerenciar drawer de busca
 *
 * Features:
 * - Abre/fecha com animacao
 * - Overlay clicavel
 * - Bloqueia scroll do body
 * - Focus trap para acessibilidade
 * - Fecha com ESC
 * - Integra com HeaderManager (fecha menu drawer ao abrir)
 * - Predictive search (busca enquanto digita)
 */
class SearchDrawerManager {
  /**
   * @param {Object} config - Configuracoes
   * @param {string} config.drawerSelector - Seletor do drawer
   * @param {string} config.toggleSelector - Seletor dos botoes toggle
   * @param {string} config.closeSelector - Seletor dos botoes fechar
   * @param {string} config.overlaySelector - Seletor do overlay
   * @param {string} config.inputSelector - Seletor do input de busca
   * @param {string} config.resultsSelector - Seletor do container de resultados
   * @param {string} config.openClass - Classe quando aberto
   * @param {string} config.bodyOpenClass - Classe no body quando aberto
   * @param {number} config.searchDelay - Delay para busca preditiva (ms)
   */
  constructor(config = {}) {
    this.config = {
      drawerSelector: '[data-search-drawer]',
      toggleSelector: '[data-search-drawer-toggle]',
      closeSelector: '[data-search-drawer-close]',
      overlaySelector: '[data-search-drawer-overlay]',
      inputSelector: '[data-search-drawer-input]',
      resultsSelector: '[data-search-drawer-results]',
      openClass: 'is-open',
      bodyOpenClass: 'search-drawer-open',
      searchDelay: 300,
      ...config
    };

    this.drawer = null;
    this.overlay = null;
    this.toggleButtons = [];
    this.closeButtons = [];
    this.input = null;
    this.resultsContainer = null;
    this.preSearchContainer = null;
    this.recommendationsContainer = null;
    this.recommendationsList = null;
    this.highlightsSlider = null;
    this.isOpen = false;
    this.previousActiveElement = null;
    this.searchTimeout = null;
    this.splideInstance = null;
    this.highlightsSplideInstance = null;
    this.recommendationsLoaded = false;

    // Storage key para ultimo produto visitado
    this.LAST_PRODUCT_KEY = 'searchDrawer_lastProductId';

    // Flag para controle do history state
    this.historyStatePushed = false;

    // Bind do handler do popstate para poder remover depois
    this._boundPopStateHandler = this._handlePopState.bind(this);

    this._init();
  }

  /**
   * Formata preco para moeda
   * @private
   * @param {number|string} price - Preco em centavos ou string
   * @returns {string} Preco formatado
   */
  _formatMoney(price) {
    if (typeof price === 'string' && price.includes('R$')) {
      return price;
    }

    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numericPrice);
  }

  _getImageFilenameWithoutExtension(imageUrl) {
    if (!imageUrl) return '';
    return imageUrl.split('/').pop().split('?')[0].split('.').slice(0, -1).join('.');
  }

  _isSwatchOnlyImage(imageUrl) {
    const filename = this._getImageFilenameWithoutExtension(imageUrl);
    return filename.slice(-2) === '90';
  }

  _getRenderableListingImages(product) {
    const rawImages = [];

    if (product?.image) rawImages.push(product.image);
    if (Array.isArray(product?.images)) rawImages.push(...product.images);

    return [...new Set(rawImages.filter(Boolean))].filter((imageUrl) => !this._isSwatchOnlyImage(imageUrl));
  }

  _shouldRenderProduct(product) {
    if (!this.hideUnavailableWithoutImage) return true;
    if (!product) return true;
    if (product.available === false) return false;

    return this._getRenderableListingImages(product).length > 0;
  }

  /**
   * Inicializacao
   * @private
   */
  _init() {
    this.drawer = document.querySelector(this.config.drawerSelector);
    if (!this.drawer) {
      console.warn('[SearchDrawerManager] Drawer not found');
      return;
    }

    this.overlay = this.drawer.querySelector(this.config.overlaySelector);
    this.toggleButtons = [...document.querySelectorAll(this.config.toggleSelector)];
    this.closeButtons = [...this.drawer.querySelectorAll(this.config.closeSelector)];
    this.input = this.drawer.querySelector(this.config.inputSelector);
    this.resultsContainer = this.drawer.querySelector(this.config.resultsSelector);
    this.hideUnavailableWithoutImage = this.drawer.dataset.hideUnavailableWithoutImage === 'true';

    // Pre-search elements
    this.preSearchContainer = this.drawer.querySelector('[data-search-presearch]');
    this.recommendationsContainer = this.drawer.querySelector('[data-search-recommendations]');
    this.recommendationsList = this.drawer.querySelector('[data-recommendations-list]');
    this.highlightsSlider = this.drawer.querySelector('[data-highlights-slider]');

    this._bindEvents();
    this._initHighlightsSlider();
  }

  /**
   * Vincula eventos
   * @private
   */
  _bindEvents() {
    // Toggle buttons (podem estar em qualquer lugar da pagina)
    this.toggleButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.open();
      });
    });

    // Close buttons
    this.closeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });

    // Overlay click
    this.overlay?.addEventListener('click', () => this.close());

    // ESC fecha
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Busca preditiva
    this.input?.addEventListener('input', (e) => {
      this._handleSearch(e.target.value);
    });

    // Submit do form
    const form = this.input?.closest('form');
    form?.addEventListener('submit', (e) => {
      if (!this.input.value.trim()) {
        e.preventDefault();
      }
    });

    // Escuta eventos do HeaderManager para coordenacao
    if (typeof headerEvents !== 'undefined') {
      // Quando o menu drawer abrir, fecha o search drawer
      headerEvents.on('drawer:open', () => {
        if (this.isOpen) {
          this.close();
        }
      });
    }

    // Escuta o botao voltar do navegador (mobile)
    window.addEventListener('popstate', this._boundPopStateHandler);
  }

  /**
   * Handler para o evento popstate (botao voltar do navegador)
   * Fecha o drawer em vez de navegar para a pagina anterior
   * @private
   * @param {PopStateEvent} event
   */
  _handlePopState() {
    // Se o drawer esta aberto e o state nao e do drawer, fecha
    if (this.isOpen && this.historyStatePushed) {
      // Evita que o close() faca history.back() novamente
      this.historyStatePushed = false;
      this._closeDrawer();
    }
  }

  /**
   * Handler para busca preditiva
   * @private
   * @param {string} query - Termo de busca
   */
  _handleSearch(query) {
    clearTimeout(this.searchTimeout);

    if (!query.trim()) {
      this._clearResults();
      return;
    }

    this.searchTimeout = setTimeout(() => {
      this._fetchResults(query);
    }, this.config.searchDelay);
  }

  /**
   * Busca resultados via API do Shopify
   * @private
   * @param {string} query - Termo de busca
   */
  async _fetchResults(query) {
    if (!this.resultsContainer) return;

    try {
      this.resultsContainer.innerHTML = '<p class="searchDrawer__loading p-[1.6rem] text-center">Buscando...</p>';

      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`
      );

      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();
      const products = data.resources.results.products || [];

      // Busca imagens adicionais para cada produto (para hover)
      const productsWithImages = await this._enrichProductsWithImages(products);

      this._renderResults(productsWithImages, query);
    } catch (error) {
      console.error('[SearchDrawerManager] Search error:', error);
      this.resultsContainer.innerHTML = '<p class="searchDrawer__error p-[1.6rem] text-center">Erro ao buscar. Tente novamente.</p>';
    }
  }

  /**
   * Renderiza resultados da busca em slider
   * @private
   * @param {Array} products - Produtos encontrados
   * @param {string} query - Termo de busca
   */
  _renderResults(products, query) {
    if (!this.resultsContainer) return;

    // Destroi instancia anterior do Splide
    this._destroySplide();

    // Esconde pre-search quando ha resultados
    this._showResults();

    const visibleProducts = (products || []).filter((product) => this._shouldRenderProduct(product));

    if (visibleProducts.length === 0) {
      this.resultsContainer.innerHTML = `
        <p class="searchDrawer__noResults p-[1.6rem] text-center">
          Nenhum resultado para "<strong>${query}</strong>"
        </p>
      `;
      return;
    }

    // Gera HTML dos cards
    const cardsHtml = visibleProducts.map(product => this._renderProductCard(product)).join('');

    // Estrutura do Splide
    this.resultsContainer.innerHTML = `
      <div class="searchDrawer__sliderWrapper p-[1.6rem] flex flex-col gap-[1.6rem]">
        <div class="searchDrawer__slider splide" data-search-results-slider>
          <div class="splide__track">
            <ul class="splide__list">
              ${cardsHtml}
            </ul>
          </div>
        </div>
        <a href="/search?q=${encodeURIComponent(query)}" class="searchDrawer__viewAll w-full p-[1.6rem] text-[1.1rem]/[100%] uppercase border border-current flex justify-center items-center hover:bg-black hover:text-white transition-colors duration-300">
          Ver todos os resultados
        </a>
      </div>
    `;

    // Inicializa Splide
    this._initSplide();

    // Carrega swatches de cor para cada card (após DOM atualizado)
    const cards = this.resultsContainer.querySelectorAll('.splide__slide');
    cards.forEach(card => this._loadCardColorsFromDOM(card));
  }

  /* ==========================================================================
     VARIANTES DE COR
     Espelha a lógica do ColorVariantLoader (pdp-manager.js) para search cards
     ========================================================================== */

  /**
   * Gera HTML do container de cores com swatch ativo + skeletons para irmãos.
   * @private
   * @param {Object|null} colorData - { variantId, productUrl, productTitle, color, pattern }
   * @param {number} productId - ID do produto atual (para filtrar da busca de irmãos)
   * @param {string} dataAttr - Atributo data-* do container ('data-main-colors' ou 'data-related-colors')
   * @returns {string} HTML do container, ou '' se não houver variantId
   */
  /**
   * Gera HTML do container de cores com swatch ativo + skeletons para irmãos.
   * Agora varre a imagem final 90 via JS para espelhar a PDP.
   * @private
   */
  _renderColorRow(colorData, product, dataAttr) {
    let swatchImage = null;
    if (product && product.images) {
      swatchImage = product.images.find(img => this._isSwatchOnlyImage(img));
    }
    
    if (!swatchImage && colorData && colorData.swatchImage) {
      swatchImage = colorData.swatchImage;
    }

    const variantId = colorData ? colorData.variantId : '';
    
    if (!swatchImage && !variantId) return '';

    let inner = `<span class="block w-full h-full bg-gray-200"></span>`;
    if (swatchImage) {
      // Ajustado width e height para 6
      inner = `<img src="${swatchImage}" alt="" class="w-full h-full object-cover" width="6" height="6" />`;
    }

    const url = colorData && colorData.productUrl ? colorData.productUrl : (product ? product.url : '#');
    const title = colorData && colorData.productTitle ? colorData.productTitle : (product ? product.title : '');

    // Ajustado para w-[0.6rem] h-[0.6rem]
    const activeSwatch = `
      <a href="${url}" class="productCard__colorSwatch shrink-0 block w-[0.6rem] h-[0.6rem] transition-colors" style="box-shadow: 0px 0px 2px 0.5px rgba(0, 0, 0, 0.15);" title="${title}">
        ${inner}
      </a>
    `;

    let skeletons = '';
    if (variantId) {
      // Skeletons ajustados para w-[0.6rem] h-[0.6rem]
      skeletons = `
        <span class="skeleton-pulse shrink-0 block w-[0.6rem] h-[0.6rem] bg-gray-200" data-color-skeleton></span>
        <span class="skeleton-pulse shrink-0 block w-[0.6rem] h-[0.6rem] bg-gray-200" data-color-skeleton style="animation-delay: 0.15s"></span>
        <span class="skeleton-pulse shrink-0 block w-[0.6rem] h-[0.6rem] bg-gray-200" data-color-skeleton style="animation-delay: 0.3s"></span>
      `;
    }

    const productId = product ? product.id : '';

    // Adicionado opacity-0 group-hover:opacity-100 transition-opacity duration-300
    return `
      <div
        class="productCard__colorVariants flex items-center gap-[0.4rem] pointer-events-auto transition-all duration-300 max-h-0 opacity-0 overflow-hidden mt-0 group-hover:max-h-[2rem] group-hover:opacity-100 group-hover:mt-[0.6rem]"
        ${dataAttr}
        data-variant-id="${variantId}"
        data-product-id="${productId}"
      >
        ${activeSwatch}
        ${skeletons}
        <span class="text-[1.1rem] leading-none text-gray-400 ml-[0.2rem]">+</span>
      </div>
    `;
  }

  /**
   * Gera HTML de um swatch de cor (elemento <a>).
   * @private
   * @param {Object} colorData - { productUrl, productTitle, color, pattern }
   * @returns {string}
   */
  _renderColorSwatchHtml(colorData) {
    const cls = `productCard__colorSwatch block w-[0.8rem] h-[0.8rem] transition-colors border border-black/10`;
    let inner = '';
    
    // Agora utilizamos o swatchImage que vem raspado do script JSON da PDP
    if (colorData.swatchImage) {
      inner = `<img src="${colorData.swatchImage}" alt="${colorData.productTitle || ''}" class="w-full h-full object-cover" width="8" height="8" />`;
    } else if (colorData.pattern) { 
      // Fallbacks antigos por segurança
      inner = `<img src="${colorData.pattern}" alt="${colorData.productTitle || ''}" class="w-full h-full object-cover" width="8" height="8" />`;
    } else if (colorData.color) {
      inner = `<span class="block w-full h-full" style="background-color: ${colorData.color};"></span>`;
    } else {
      inner = `<span class="block w-full h-full bg-gray-200"></span>`;
    }
    
    return `<a href="${colorData.productUrl || '#'}" class="${cls}" title="${colorData.productTitle || ''}">${inner}</a>`;
  }

  /**
   * Carrega swatches de irmãos para todos os containers de cor de um card.
   * Chamado após o card ser inserido no DOM.
   * @param {HTMLElement} card
   */
  _loadCardColorsFromDOM(card) {
    const mainContainer = card.querySelector('[data-main-colors]');
    const relatedContainer = card.querySelector('[data-related-colors]');

    if (mainContainer) this._loadColorSiblings(mainContainer);
    if (relatedContainer) this._loadColorSiblings(relatedContainer);
  }

  /**
   * Busca produtos irmãos via Predictive Search API e renderiza swatches.
   * Espelha ColorVariantLoader._fetchSiblings() de pdp-manager.js.
   * @private
   * @param {HTMLElement} container - Container com data-variant-id e data-product-id
   */
  async _loadColorSiblings(container) {
    const variantId = container.dataset.variantId;
    const currentProductId = parseInt(container.dataset.productId);

    if (!variantId) {
      this._removeColorSkeletons(container);
      return;
    }

    try {
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(variantId)}&resources[type]=product&resources[limit]=25`
      );
      const data = await response.json();
      const products = data.resources?.results?.products || [];
      const siblings = products.filter(p => p.id !== currentProductId);

      if (siblings.length === 0) {
        this._removeColorSkeletons(container);
        return;
      }

      let pending = siblings.length;
      const onComplete = () => {
        pending--;
        if (pending <= 0) this._removeColorSkeletons(container);
      };

      siblings.forEach(prod => this._fetchSiblingColorData(prod, variantId, container, onComplete));
    } catch (err) {
      console.error('[SearchDrawerManager:Color] Erro na Search API:', err);
      this._removeColorSkeletons(container);
    }
  }

  /**
   * Busca dados de cor de um produto via Section Rendering API e renderiza swatch.
   * Espelha ColorVariantLoader._fetchColorData() de pdp-manager.js.
   * @private
   */
  async _fetchSiblingColorData(prod, variantId, container, onComplete) {
    try {
      const response = await fetch(`/products/${prod.handle}?sections=pdp-main`);
      const sections = await response.json();
      const html = sections['pdp-main'];
      if (!html) { onComplete(); return; }

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const script = doc.querySelector('[data-pdp-color-data]');
      if (!script) { onComplete(); return; }

      const colorData = JSON.parse(script.textContent);
      if (colorData.variantId !== variantId) { onComplete(); return; }

      const swatch = document.createElement('a');
      swatch.href = colorData.productUrl;
      swatch.className = 'productCard__colorSwatch shrink-0 block w-[0.6rem] h-[0.6rem] transition-colors border border-black/10';
      swatch.title = colorData.productTitle;
      swatch.title = colorData.productTitle;
      swatch.style.opacity = '0';
      swatch.style.transition = 'opacity 0.3s ease';

      if (colorData.pattern) {
        swatch.innerHTML = `<img src="${colorData.pattern}" alt="${colorData.productTitle}" class="w-full h-full object-cover" width="16" height="16" />`;
      } else if (colorData.color) {
        swatch.innerHTML = `<span class="block w-full h-full" style="background-color: ${colorData.color};"></span>`;
      }

      // Insere antes dos skeletons para manter a ordem visual correta
      const firstSkeleton = container.querySelector('[data-color-skeleton]');
      if (firstSkeleton) {
        container.insertBefore(swatch, firstSkeleton);
      } else {
        container.appendChild(swatch);
      }

      // Fade in suave após inserção no DOM
      requestAnimationFrame(() => {
        requestAnimationFrame(() => { swatch.style.opacity = '1'; });
      });

      onComplete();
    } catch (err) {
      console.error('[SearchDrawerManager:Color] Erro ao buscar cor:', err);
      onComplete();
    }
  }

  /**
   * Remove os skeletons de um container de cores.
   * @private
   */
  _removeColorSkeletons(container) {
    container.querySelectorAll('[data-color-skeleton]').forEach(el => {
      el.style.transition = 'opacity 0.2s ease';
      el.style.opacity = '0';
      setTimeout(() => el.remove(), 200);
    });
  }

  /**
   * Gera HTML dos botões de tamanho a partir de variantes e nomes de opções.
   * Espelha a lógica Liquid de product-card.liquid.
   * @private
   * @param {Array} variants - Variantes do produto (com id, available, options[])
   * @param {Array<string>} options - Nomes das opções do produto
   * @returns {string} HTML dos botões
   */
  _renderSizeButtons(variants, options) {
    if (!variants || variants.length === 0) {
      return this._renderFallbackSizeButtons();
    }

    let sizeIndex = 0;
    let foundSizeOption = false;
    if (options) {
      for (let i = 0; i < options.length; i++) {
        const name = (options[i] || '').toLowerCase();
        if (name.includes('tamanho') || name.includes('size') || name === 'tam') {
          sizeIndex = i;
          foundSizeOption = true;
          break;
        }
      }
      if (!foundSizeOption && variants.length > 1) {
        sizeIndex = options.length - 1;
      }
    }

    const seen = new Set();
    let html = '';
    for (const variant of variants) {
      let sizeLabel = (variant.options || [])[sizeIndex] || '';
      if (sizeLabel === 'Default Title') sizeLabel = 'Tamanho Único';
      if (!sizeLabel || seen.has(sizeLabel)) continue;
      seen.add(sizeLabel);
      const isUnavailable = !variant.available;
      const cls = `productCard__sizeOption hover:opacity-70 transition-opacity cursor-pointer bg-transparent border-none p-0${isUnavailable ? ' opacity-30' : ''}`;
      html += `<button type="button" class="${cls}" data-size="${sizeLabel}" data-variant-id="${variant.id}">${sizeLabel}</button>`;
    }
    return html || this._renderFallbackSizeButtons();
  }

  /**
   * Gera HTML do fallback de tamanhos (PP/P/M/G/GG) quando não há dados de variantes.
   * @private
   * @returns {string}
   */
  _renderFallbackSizeButtons() {
    return ['PP', 'P', 'M', 'G', 'GG'].map((s, i) =>
      `<button type="button" class="productCard__sizeOption${i === 2 ? ' font-bold' : ''} hover:opacity-70 transition-opacity cursor-pointer bg-transparent border-none p-0" data-size="${s}">${s}</button>`
    ).join('');
  }

  /**
   * Renderiza card de produto individual
   *
   * ╔═══════════════════════════════════════════════════════════════════════════╗
   * ║  ⚠️  ATENÇÃO: ESTRUTURA HTML DUPLICADA EM LIQUID!                         ║
   * ╠═══════════════════════════════════════════════════════════════════════════╣
   * ║  Arquivo: snippets/product-card.liquid                             ║
   * ║                                                                           ║
   * ║  🔄 Ao alterar a ESTRUTURA HTML, ATUALIZE TAMBÉM o snippet Liquid!        ║
   * ║                                                                           ║
   * ║  💡 Comportamento interativo gerenciado por: assets/product-manager.js    ║
   * ╚═══════════════════════════════════════════════════════════════════════════╝
   *
   * @private
   * @param {Object} product - Dados do produto
   * @param {Object|null} [product.related_product] - Produto relacionado (metafield pdp.related_product).
   *   Não disponível via /search/suggest.json — enriquecer via _enrichProductsWithImages se necessário.
   * @returns {string} HTML do card
   */
  _renderProductCard(product) {
    const hasComparePrice = product.compare_at_price && product.compare_at_price !== product.price;
    const discountPercent = hasComparePrice
      ? Math.round(((product.compare_at_price - product.price) * 100) / product.compare_at_price)
      : null;
      const formattedPrice = this._formatMoney(product.price);
      const formattedComparePrice = hasComparePrice ? this._formatMoney(product.compare_at_price) : '';

      const renderableImages = this._getRenderableListingImages(product);
      const primaryImage = renderableImages[0] || null;
      const hoverImage = renderableImages[1] || null;
      const hasHoverImage = Boolean(hoverImage);

    // Produto relacionado (metafield pdp.related_product — null quando vem da Search API)
    const related = product.related_product || null;
    const relatedHasComparePrice = related && related.compare_at_price && related.compare_at_price !== related.price;
    const relatedFormattedPrice = related ? this._formatMoney(related.price) : '';
    const relatedFormattedComparePrice = relatedHasComparePrice ? this._formatMoney(related.compare_at_price) : '';

    // Cores do produto principal (aparecem no info section ao expandir o popup)
    const mainColorRow = this._renderColorRow(
      product.color_data || null,
      product,
      'data-main-colors'
    );

    // Cores do produto relacionado (aparecem no info section ao expandir o popup)
    const relatedColorData = related ? {
      variantId: related.variant_id || null,
      productUrl: related.url,
      productTitle: related.title,
      color: related.color || null,
      pattern: related.pattern || null
    } : null;
    
    const relatedColorRow = related
      ? this._renderColorRow(relatedColorData, related, 'data-related-colors') // <-- ANTES ERA related.product_id
      : '';

    return `
      <li class="splide__slide">
        <div class="productCard @container group flex flex-col w-full shrink-0 no-underline text-inherit relative">
          <!-- <a href="${product.url}" class="productCard__link absolute inset-0 z-0" aria-label="${product.title}"></a> -->

          <div class="productCard__imageContainer w-full relative aspect-[4/5] bg-gray-100 overflow-hidden">
            ${primaryImage ? `
              <a href="${product.url}" class="productCard__imageLink block w-full h-full">
                <img
                  class="productCard__image productCard__image--primary w-full h-full object-cover absolute inset-0 transition-opacity duration-300${hasHoverImage ? ' group-hover:opacity-0' : ''}"
                  src="${primaryImage}"
                  alt="${product.title}"
                  loading="lazy"
                  width="600"
                  height="750"
                />
                ${hasHoverImage ? `
                  <img
                    class="productCard__image productCard__image--hover w-full h-full object-cover absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                    src="${hoverImage}"
                    alt="${product.title}"
                    loading="lazy"
                    width="600"
                    height="750"
                  />
                ` : ''}
              </a>
            ` : ''}

            ${discountPercent ? `
            <div class="productCard__badge absolute top-[1rem] left-[1rem] @[24rem]:top-[1.6rem] @[24rem]:left-[1.6rem] z-[1] opacity-50 bg-white/90 backdrop-blur-lg px-[0.8rem] py-[0.4rem] text-[1rem] font-medium uppercase">
              -${discountPercent}%
            </div>
            ` : ''}

            <div
              class="productCard__sizePopup absolute bottom-[5.3rem] left-[1rem] right-[1rem] @[24rem]:bottom-[5rem] @[24rem]:left-[1.6rem] @[24rem]:right-[1.6rem] bg-white/60 backdrop-blur-lg px-[1rem] py-[1.6rem] @[24rem]:p-[1.6rem] flex flex-col gap-[2.4rem] transition-all duration-300 opacity-0 invisible translate-y-[0.5rem] z-10 pointer-events-auto"
              data-size-popup
            >
              <div class="productCard__mainSizes flex flex-col gap-[1.2rem]">
                <span class="productCard__popupTitle text-[0.9rem] text-black truncate">
                  ${product.title}
                </span>

                <div class="productCard__sizes flex items-center gap-[1.6rem] text-[1rem]/[100%] text-black">
                  ${this._renderSizeButtons(product.variants, product.options)}
                </div>
              </div>

              ${related ? `
                <div class="productCard__relatedSizes flex flex-col gap-[1.2rem]">
                  <span class="productCard__popupTitle text-[0.9rem] text-black truncate">
                    ${related.title}
                  </span>

                  <div class="productCard__sizes flex items-center gap-[1.6rem] text-[1rem]/[100%] text-black">
                    ${this._renderSizeButtons(related.variants, related.options)}
                  </div>
                </div>
              ` : ''}
            </div>

            <button
              type="button"
              class="productCard__addButton absolute bottom-[1rem] left-[1rem] w-[3.3rem] h-[3.3rem] @[24rem]:bottom-[1.6rem] @[24rem]:left-[1.6rem] @[24rem]:w-[2.4rem] @[24rem]:h-[2.4rem] opacity-50 bg-white/90 backdrop-blur-lg flex items-center justify-center hover:bg-white hover:opacity-100 transition-all cursor-pointer border-none z-20 pointer-events-auto"
              aria-label="Adicionar ao carrinho"
              data-product-add-toggle
            >
              <svg class="productCard__addIcon w-[1.1rem] h-[1.1rem] transition-transform duration-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 5v14"/>
                <path d="M5 12h14"/>
              </svg>
            </button>

            ${product.color_data?.hasSizeIcon ? `
              <svg class="productCard__adjustableIcon absolute top-[1rem] right-[1rem] @[24rem]:top-[1.6rem] @[24rem]:right-[1.6rem] z-[2] w-[2rem] h-[1.9rem] pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 23" fill="none">
                <g clip-path="url(#clip_reg_${product.id})">
                  <line x1="4.77832" y1="16.585" x2="4.77832" y2="0.000171629" stroke="black" stroke-width="1.5"/>
                  <rect x="0.75" y="9.04199" width="7.71377" height="4.02826" rx="1.25" fill="#F2F0EF" stroke="black" stroke-width="1.5"/>
                  <line x1="7.37109" y1="18.5986" x2="23.9559" y2="18.5986" stroke="black" stroke-width="1.5"/>
                  <rect x="20.4417" y="14.5713" width="7.71377" height="4.02826" rx="1.25" transform="rotate(90 20.4417 14.5713)" fill="#F2F0EF" stroke="black" stroke-width="1.5"/>
                </g>
                <defs>
                  <clipPath id="clip_reg_${product.id}">
                    <rect width="24" height="23" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
            ` : ''}
          </div>

          <div class="productCard__infoList pt-[1.6rem] pb-[0.8rem] text-[1.2rem] flex flex-col">
            <div class="productCard__info relative z-10 flex flex-col gap-[0.8rem]">
              <a href="${product.url}" class="productCard__infoRow flex-wrap flex flex-col sm:flex-row justify-between items-start sm:items-center gap-x-[1.6rem] gap-y-[0.8rem] no-underline text-inherit hover:opacity-70 transition-opacity">
                <span class="productCard__title">${product.title}</span>
                <div class="productCard__prices flex items-center gap-[0.8rem] shrink-0">
                  ${hasComparePrice ? `<span class="productCard__comparePrice line-through text-black/50">${formattedComparePrice}</span>` : ''}
                  <span class="productCard__price">${formattedPrice}</span>
                </div>
              </a>

              ${mainColorRow}
            </div>

            ${related ? `
              <a
                href="${related.url}"
                class="productCard__info productCard__info--secondary relative z-10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-x-[1.6rem] gap-y-[0.8rem] mt-[0.8rem] text-inherit no-underline hover:opacity-60 transition-opacity"
              >
                <span class="productCard__relatedTitle">${related.title}</span>
                <div class="productCard__prices flex items-center gap-[0.8rem] shrink-0">
                  ${relatedHasComparePrice ? `<span class="productCard__comparePrice line-through text-black/50">${relatedFormattedComparePrice}</span>` : ''}
                  <span class="productCard__price">${relatedFormattedPrice}</span>
                </div>
              </a>
              ${relatedColorRow}
            ` : ''}
          </div>
        </div>
      </li>
    `;
  }

  /**
   * Inicializa Splide slider
   * @private
   */
  _initSplide() {
    const sliderElement = this.resultsContainer.querySelector('[data-search-results-slider]');
    if (!sliderElement || typeof Splide === 'undefined') {
      console.warn('[SearchDrawerManager] Splide not available');
      return;
    }

    this.splideInstance = new Splide(sliderElement, {
      type: 'slide',
      perPage: 1,
      fixedWidth: '30rem',
      gap: '1.6rem',
      pagination: false,
      arrows: false,
      drag: 'free',
      snap: true,
      breakpoints:{
        400:{
          fixedWidth: '90%',
        }
      }
    });

    // Blur input ao iniciar drag (evita teclado no mobile)
    this.splideInstance.on('drag', () => {
      if (this.input && document.activeElement === this.input) {
        this.input.blur();
      }
    });

    this.splideInstance.mount();
  }

  /**
   * Destroi instancia do Splide
   * @private
   */
  _destroySplide() {
    if (this.splideInstance) {
      this.splideInstance.destroy();
      this.splideInstance = null;
    }
  }

  /**
   * Enriquece produtos com imagens adicionais para hover
   * @private
   * @param {Array} products - Lista de produtos da busca
   * @returns {Promise<Array>} Produtos com array de imagens
   */
  async _enrichProductsWithImages(products) {
    if (!products || products.length === 0) return products;

    // Busca detalhes de todos os produtos em paralelo
    const enrichedProducts = await Promise.all(
      products.map(async (product) => {
        try {
          // Extrai o handle do produto da URL
          const urlParts = product.url.split('/');
          const handleIndex = urlParts.indexOf('products');
          if (handleIndex === -1 || !urlParts[handleIndex + 1]) {
            return product;
          }

          const handle = urlParts[handleIndex + 1].split('?')[0];

          // Busca JSON do produto (imagens) e HTML da página (produto relacionado) em paralelo
          const [jsonResponse, htmlResponse] = await Promise.all([
            fetch(`/products/${handle}.json`),
            fetch(`/products/${handle}`)
          ]);

          let enriched = { ...product };

          // Imagens, variantes e opções via .json
          if (jsonResponse.ok) {
            const data = await jsonResponse.json();
            if (data.product?.images?.length) {
              enriched.images = data.product.images.map(img => img.src);
              enriched.image = enriched.images[0] || enriched.image;
            }
            if (data.product?.variants) {
              enriched.variants = data.product.variants.map(v => ({
                id: v.id,
                available: v.available,
                options: [v.option1, v.option2, v.option3]
              }));
            }
            if (typeof data.product?.available === 'boolean') {
              enriched.available = data.product.available;
            }
            if (data.product?.options) {
              enriched.options = data.product.options.map(o => o.name);
            }
          }

          // Dados de cor e produto relacionado via HTML da página
          if (htmlResponse.ok) {
            const html = await htmlResponse.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            const colorScript = doc.querySelector('[data-pdp-color-data]');
            if (colorScript) {
              try { enriched.color_data = JSON.parse(colorScript.textContent); } catch (_) {}
            }

            const relatedScript = doc.querySelector('[data-related-product-json]');
            if (relatedScript) {
              try { enriched.related_product = JSON.parse(relatedScript.textContent); } catch (_) {}
            }
          }

          return enriched;
        } catch (error) {
          // Em caso de erro, retorna produto original
          return product;
        }
      })
    );

    return enrichedProducts;
  }

  /* ==========================================================================
     PRE-SEARCH: Recomendações e Destaques
     ========================================================================== */

  /**
   * Inicializa slider de destaques (Liquid-rendered)
   * @private
   */
  _initHighlightsSlider() {
    if (!this.highlightsSlider || typeof Splide === 'undefined') {
      return;
    }

    this.highlightsSplideInstance = new Splide(this.highlightsSlider, {
      type: 'slide',
      perPage: 1,
      fixedWidth: '30rem',
      gap: '1.6rem',
      pagination: false,
      arrows: false,
      snap: true,
      breakpoints: {
        500: {
          fixedWidth: false,
          perPage: 2,
          perMove: 1,
        },
        350: {
          fixedWidth: '90%',
          perPage: 1,
        },
      }
    });

    // Blur input ao iniciar drag
    this.highlightsSplideInstance.on('drag', () => {
      if (this.input && document.activeElement === this.input) {
        this.input.blur();
      }
    });

    this.highlightsSplideInstance.mount();

    // Carrega cores para cards Liquid pré-renderizados (remove skeletons após fetch)
    this.highlightsSlider.querySelectorAll('.productCard').forEach(card => {
      this._loadCardColorsFromDOM(card);
    });
  }

  /**
   * Destroi slider de destaques
   * @private
   */
  _destroyHighlightsSlider() {
    if (this.highlightsSplideInstance) {
      this.highlightsSplideInstance.destroy();
      this.highlightsSplideInstance = null;
    }
  }

  /**
   * Carrega recomendações da API Shopify
   * @private
   */
  async _loadRecommendations() {
    if (!this.recommendationsContainer || !this.recommendationsList) return;
    if (this.recommendationsLoaded) return;

    const anchorProductId = this._getAnchorProductId();
    if (!anchorProductId) {
      this._hideRecommendations();
      return;
    }

    const limit = this.recommendationsContainer.dataset.recommendationsLimit || 5;

    try {
      const response = await fetch(
        `/recommendations/products.json?product_id=${anchorProductId}&limit=${limit}&intent=related`
      );

      if (!response.ok) throw new Error('Recommendations fetch failed');

      const data = await response.json();
      this._renderRecommendations(data.products);
      this.recommendationsLoaded = true;
    } catch (error) {
      console.warn('[SearchDrawerManager] Recommendations error:', error);
      this._hideRecommendations();
    }
  }

  /**
   * Obtem o ID do produto ancora para recomendações
   * Prioridade: ultimo produto visitado > produto ancora configurado
   * @private
   * @returns {string|null}
   */
  _getAnchorProductId() {
    // 1. Tenta obter ultimo produto visitado do localStorage
    const lastProductId = localStorage.getItem(this.LAST_PRODUCT_KEY);
    if (lastProductId) {
      return lastProductId;
    }

    // 2. Fallback: produto ancora configurado no schema
    const anchorProductId = this.recommendationsContainer?.dataset.anchorProductId;
    if (anchorProductId) {
      return anchorProductId;
    }

    return null;
  }

  /**
   * Renderiza lista de recomendações (apenas texto/links)
   * @private
   * @param {Array} products
   */
  _renderRecommendations(products) {
    if (!this.recommendationsList) return;

    if (!products || products.length === 0) {
      this._hideRecommendations();
      return;
    }

    const linksHtml = products.map(product => `
      <a
        href="${product.url}"
        class="searchDrawer__recommendationLink text-[1.2rem]/[100%] text-black no-underline hover:underline normal-case"
      >
        ${product.title}
      </a>
    `).join('');

    this.recommendationsList.innerHTML = linksHtml;
  }

  /**
   * Esconde container de recomendações
   * @private
   */
  _hideRecommendations() {
    if (this.recommendationsContainer) {
      this.recommendationsContainer.classList.add('hidden');
    }
  }

  /**
   * Mostra container de recomendações
   * @private
   */
  _showRecommendations() {
    if (this.recommendationsContainer) {
      this.recommendationsContainer.classList.remove('hidden');
    }
  }

  /**
   * Esconde pre-search e mostra resultados
   * @private
   */
  _showResults() {
    this.preSearchContainer?.classList.add('hidden');
    this.resultsContainer?.classList.remove('hidden');
  }

  /**
   * Mostra pre-search e esconde resultados
   * @private
   */
  _showPreSearch() {
    this.preSearchContainer?.classList.remove('hidden');
    this.resultsContainer?.classList.add('hidden');
    this._showRecommendations();
  }

  /**
   * Limpa resultados
   * @private
   */
  _clearResults() {
    this._destroySplide();
    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = '';
    }
    // Mostra pre-search quando busca e limpa
    this._showPreSearch();
  }

  /**
   * Abre o drawer
   */
  open() {
    if (!this.drawer || this.isOpen) return;

    // Fecha o menu drawer primeiro (via evento)
    if (typeof headerEvents !== 'undefined') {
      headerEvents.emit('search-drawer:open', { drawer: this });
    }

    // Fecha o menu drawer diretamente, sem history.back()
    // Se o drawer tem um state no histórico, substitui pelo state do search via replaceState
    // para manter uma única entrada limpa no histórico (sem ghost states)
    if (window.HeaderManager?.drawer?.isOpen) {
      const menuDrawer = window.HeaderManager.drawer;
      if (menuDrawer.historyStatePushed) {
        // Substitui o state do drawer pelo do search na mesma posição do histórico
        // replaceState não dispara popstate — evita interferência com _handlePopState
        history.replaceState({ searchDrawerOpen: true }, '', window.location.href);
        this.historyStatePushed = true; // já registrado via replaceState
      }
      menuDrawer.forceClose(); // fecha sem history.back()
    }

    // Salva elemento focado para restaurar depois
    this.previousActiveElement = document.activeElement;

    this.isOpen = true;
    this.drawer.classList.add(this.config.openClass);
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add(this.config.bodyOpenClass);
    document.body.style.overflow = 'hidden';

    // Foca no input de busca apenas em desktop
    // Em mobile, evita abrir o teclado para permitir visualização dos destaques
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    if (isDesktop) {
      requestAnimationFrame(() => {
        this.input?.focus();
      });
    }

    // Carrega recomendações (apenas uma vez)
    this._loadRecommendations();

    // Adiciona estado ao history para capturar o botao voltar (mobile)
    if (!this.historyStatePushed) {
      history.pushState({ searchDrawerOpen: true }, '', window.location.href);
      this.historyStatePushed = true;
    }
  }

  /**
   * Fecha o drawer (metodo publico)
   * Se o history state foi adicionado, usa history.back() para manter consistencia
   */
  close() {
    if (!this.drawer || !this.isOpen) return;

    // Se adicionamos um state ao history, usamos back() para fechar
    // Isso vai disparar popstate -> _handlePopState -> _closeDrawer
    if (this.historyStatePushed) {
      history.back();
      return;
    }

    // Caso contrario, fecha diretamente
    this._closeDrawer();
  }

  /**
   * Fecha o drawer (logica interna)
   * @private
   */
  _closeDrawer() {
    if (!this.drawer) return;

    this.isOpen = false;
    this.drawer.classList.remove(this.config.openClass);
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove(this.config.bodyOpenClass);
    document.body.style.overflow = '';

    // Limpa busca
    if (this.input) {
      this.input.value = '';
    }
    this._clearResults();

    // Restaura foco
    this.previousActiveElement?.focus();

    // Emite evento
    if (typeof headerEvents !== 'undefined') {
      headerEvents.emit('search-drawer:close', { drawer: this });
    }
  }

  /**
   * Alterna estado do drawer
   */
  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  /**
   * Destroi a instancia
   */
  destroy() {
    this.close();
    this._destroySplide();
    this._destroyHighlightsSlider();
    clearTimeout(this.searchTimeout);

    // Remove listener do popstate
    window.removeEventListener('popstate', this._boundPopStateHandler);
  }
}

/* ==========================================================================
   INICIALIZACAO
   ========================================================================== */

// Aguarda DOM e inicializa
function initSearchDrawerManager() {
  window.SearchDrawerManager = new SearchDrawerManager({
    drawerSelector: '[data-search-drawer]',
    toggleSelector: '[data-search-drawer-toggle]',
    closeSelector: '[data-search-drawer-close]',
    overlaySelector: '[data-search-drawer-overlay]',
    inputSelector: '[data-search-drawer-input]',
    resultsSelector: '[data-search-drawer-results]'
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSearchDrawerManager);
} else {
  initSearchDrawerManager();
}

// Suporte ao Theme Editor
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const isSearchDrawer = event.target.querySelector('[data-search-drawer]');
    if (isSearchDrawer) {
      window.SearchDrawerManager?.destroy();
      initSearchDrawerManager();
    }
  });
}

/* ==========================================================================
   PRODUCT TRACKING
   Rastreia ultimo produto visitado para recomendações
   ========================================================================== */

/**
 * Salva o ID do produto atual no localStorage
 * Chamado automaticamente em paginas de produto
 */
function trackLastVisitedProduct() {
  // Verifica se estamos em uma pagina de produto
  const productMeta = document.querySelector('meta[property="og:type"][content="product"]');
  if (!productMeta) return;

  // Tenta obter o ID do produto de diferentes fontes
  let productId = null;

  // 1. Via meta tag do produto
  const productIdMeta = document.querySelector('meta[name="product-id"]');
  if (productIdMeta) {
    productId = productIdMeta.content;
  }

  // 2. Via ShopifyAnalytics (se disponivel)
  if (!productId && window.ShopifyAnalytics?.meta?.product?.id) {
    productId = window.ShopifyAnalytics.meta.product.id;
  }

  // 3. Via meta:product:id (Schema.org)
  if (!productId) {
    const schemaProductId = document.querySelector('meta[property="product:id"]');
    if (schemaProductId) {
      productId = schemaProductId.content;
    }
  }

  // 4. Via URL pattern /products/handle e fetch API
  if (!productId) {
    const productMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (productMatch) {
      // Busca o ID via API
      fetch(`${window.location.pathname}.json`)
        .then(res => res.json())
        .then(data => {
          if (data.product?.id) {
            localStorage.setItem('searchDrawer_lastProductId', String(data.product.id));
          }
        })
        .catch(() => {});
      return; // Retorna pois o salvamento sera async
    }
  }

  if (productId) {
    localStorage.setItem('searchDrawer_lastProductId', String(productId));
  }
}

// Executa tracking ao carregar a pagina
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', trackLastVisitedProduct);
} else {
  trackLastVisitedProduct();
}

// Export para modulos ES6
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { SearchDrawerManager, trackLastVisitedProduct };
}

// Export para modulos ES6
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { SearchDrawerManager, trackLastVisitedProduct };
}
